/**
 * useAccessibility — global accessibility preferences
 *
 * Stores high-contrast / large-text / reduce-motion preferences and
 * applies matching classes (`a11y-high-contrast`, `a11y-large-text`,
 * `a11y-reduce-motion`) to the <html> element so global CSS can react.
 *
 * Persistence model:
 *  - localStorage is always written so prefs survive a refresh and are
 *    available before any network call resolves.
 *  - When a user is signed in we additionally GET their stored prefs from
 *    `/api/user/accessibility` on mount (overriding the local copy if the
 *    server has a record) and PATCH the same endpoint on every change so
 *    the choices follow them across devices and sessions.
 */

import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useAuth } from './useAuth';
import { apiRequest } from '@/lib/queryClient';

export interface AccessibilityPrefs {
  highContrast: boolean;
  largeText: boolean;
  reduceMotion: boolean;
}

const STORAGE_KEY = 'tabib_accessibility_prefs';
const DEFAULTS: AccessibilityPrefs = {
  highContrast: false,
  largeText: false,
  reduceMotion: false,
};

interface AccessibilityContextValue {
  prefs: AccessibilityPrefs;
  toggle: (key: keyof AccessibilityPrefs) => void;
  set: (key: keyof AccessibilityPrefs, value: boolean) => void;
  reset: () => void;
}

const AccessibilityContext = createContext<AccessibilityContextValue | null>(null);

function readStoredPrefs(): AccessibilityPrefs {
  if (typeof window === 'undefined') return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return readSystemDefaults();
    const parsed = JSON.parse(raw);
    return {
      highContrast: !!parsed.highContrast,
      largeText: !!parsed.largeText,
      reduceMotion: !!parsed.reduceMotion,
    };
  } catch {
    return readSystemDefaults();
  }
}

function readSystemDefaults(): AccessibilityPrefs {
  if (typeof window === 'undefined') return DEFAULTS;
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  const highContrast = window.matchMedia?.('(prefers-contrast: more)').matches ?? false;
  return { ...DEFAULTS, reduceMotion, highContrast };
}

function applyPrefsToDocument(prefs: AccessibilityPrefs) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('a11y-high-contrast', prefs.highContrast);
  root.classList.toggle('a11y-large-text', prefs.largeText);
  root.classList.toggle('a11y-reduce-motion', prefs.reduceMotion);
}

function isSamePrefs(a: AccessibilityPrefs, b: AccessibilityPrefs): boolean {
  return (
    a.highContrast === b.highContrast &&
    a.largeText === b.largeText &&
    a.reduceMotion === b.reduceMotion
  );
}

export function AccessibilityProvider({ children }: { children: ReactNode }): JSX.Element {
  const { isAuthenticated } = useAuth();
  const [prefs, setPrefs] = useState<AccessibilityPrefs>(DEFAULTS);
  // Tracks the last value we synced to the server so we don't echo back a
  // freshly-fetched server response as if the user had toggled it.
  const lastServerSyncRef = useRef<AccessibilityPrefs | null>(null);

  // Initial mount — load from localStorage and apply immediately.
  useEffect(() => {
    const initial = readStoredPrefs();
    setPrefs(initial);
    applyPrefsToDocument(initial);
  }, []);

  // Server sync (read) — when authenticated, fetch the canonical copy from
  // the server. If the server has a record it wins; otherwise we leave the
  // local prefs in place and the next change will create a server row.
  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated) return;
    (async () => {
      try {
        const res = await apiRequest('GET', '/api/user/accessibility');
        const json = await res.json();
        if (cancelled) return;
        if (json?.prefs) {
          const remote: AccessibilityPrefs = {
            highContrast: !!json.prefs.highContrast,
            largeText: !!json.prefs.largeText,
            reduceMotion: !!json.prefs.reduceMotion,
          };
          lastServerSyncRef.current = remote;
          setPrefs(remote);
          applyPrefsToDocument(remote);
        }
      } catch {
        /* offline — keep local prefs */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Persist locally on every change, and PATCH the server when signed in.
  useEffect(() => {
    applyPrefsToDocument(prefs);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      /* ignore quota errors */
    }
    if (!isAuthenticated) return;
    if (lastServerSyncRef.current && isSamePrefs(lastServerSyncRef.current, prefs)) {
      return;
    }
    lastServerSyncRef.current = prefs;
    apiRequest('PATCH', '/api/user/accessibility', prefs).catch(() => {
      /* best-effort — local copy still authoritative on this device */
    });
  }, [prefs, isAuthenticated]);

  const value: AccessibilityContextValue = {
    prefs,
    toggle: (key) => setPrefs((p) => ({ ...p, [key]: !p[key] })),
    set: (key, value) => setPrefs((p) => ({ ...p, [key]: value })),
    reset: () => setPrefs(DEFAULTS),
  };

  return <AccessibilityContext.Provider value={value}>{children}</AccessibilityContext.Provider>;
}

export function useAccessibility(): AccessibilityContextValue {
  const ctx = useContext(AccessibilityContext);
  if (!ctx) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return ctx;
}
